import { CoverageIndicator, DataLoadError, Divider, Loader, PrintView } from '@creditsights/styleguide-web';
// eslint-disable-next-line import/no-unresolved
import { DealMarketStatus, getDealsTabs } from '@creditsights/cs-web';
import classNames from 'classnames/bind';
import isEmpty from 'lodash/isEmpty';
import React, { useEffect, useState, useRef } from 'react';
import { Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ROUTES } from '../../Base.constants';
import LinkGroup from '../../components/LinkGroup/LinkGroup';
import AnalystsWidget from '../../components/widgets/Analysts';
import { hasCompanyRiskMetrics } from '../../components/widgets/RiskProducts/RiskProductsWidget.logic';
import useFetchAnalysts from '../../hooks/analyst/useFetchAnalysts';
import useFetchCompanyDeals from '../../hooks/deals/useFetchCompanyDeals';
import useFetchCases from '../../hooks/dockets/useFetchCases';
import useFetchCompanyDocuments from '../../hooks/documents/useFetchCompanyDocuments';
import useFetchFinancials from '../../hooks/financials/useFetchFinancials';
import useFetchFinancialsSegments from '../../hooks/financials/useFetchFinancialsSegments';
import { useFetchIssuer, useFetchPageComponents } from '../../hooks/issuers';
import useFetchUserPermissions from '../../hooks/permissions/useFetchUserPermissions';
import { useFetchForPrint } from '../../hooks/print';
import useFetchRiskProducts from '../../hooks/riskProducts/useFetchRiskProducts';
import useFetchFilters from '../../hooks/tags/useFetchFilters';
import { IssuerType } from '../../models';
import { CompanyPageParams } from '../../models/CompanyPageParams';
import { IssuerDto } from '../../services/dtos/issuer/issuer.dto';
import { getIssuerRoute, isNotFound } from '../../utils/navigationUtils';
import {
  hasPermissionToCoreScores,
  hasPermissionToDockets,
  hasPermissionToFinancials,
  getCustomDealPermissions,
} from '../../utils/permissionsUtils';
import { getIssuerTag } from '../../utils/tagUtils';
import { safeDecode } from '../../utils/urlUtils';
import {
  getIssuerRoutes,
  isCompanyCovered,
  isCompanyRoute,
  isDealsRoute,
  hideCSView,
  isDocketsRoute,
  isFinancialsCovered,
  isRpCovered,
  isCompanyCoveredRPOnly,
  isAAAButtonVisible,
} from './Company.logic';
import CompanyAddToWatchlistAction from './CompanyAddToWatchlistAction/CompanyAddToWatchlistAction';
import { CompanyDealsContextProvider } from './CompanyDealsContext';
import CompanyHeader from './CompanyHeader';
import CompanyOverviewTab from './CompanyOverviewTab';
import CompanyPrintAction from './CompanyPrintAction';
import { STATIC_PRINTABLE_SECTIONS } from './CompanyPrintAction/CompanyPrintAction.constants';
import { RecentlyPublishedContextProvider } from './CompanyRecentlyPublishedContext';
import { CompanyRiskProductsContextProvider } from './CompanyRiskProductsContext';
import styles from './Company.module.scss';
import { trackCompanyTabView } from '../../utils/trackUsageUtils';
import CompanyDownloadSnapshotAction from './CompanyDownloadSnapshotAction';
import useFetchCompanySnapshot from '../../hooks/content/useFetchCompanySnapshot';
import useFetchUserProfile from '../../hooks/profile/useFetchUserProfile';
import { setScoreDataFormat } from '../../utils/scoreUtils';

interface CompanyProps {
  issuerType: IssuerType;
}

export interface CompanyOutletContext {
  issuerTag: string;
  company: IssuerDto;
}

const cx = classNames.bind(styles);

const Company = ({ issuerType }: CompanyProps): React.ReactElement => {
  const companyPrintViewRef = useRef<HTMLElement | null>(null);
  const [selectedSectionsForPrint, setSelectedSectionsForPrint] = useState<string[]>([]);

  const [isAnalystsWidgetRendered, setIsAnalystsWidgetRendered] = useState<boolean>(false);
  const { issuerId } = useParams() as CompanyPageParams;
  const navigate = useNavigate();
  const issuerTag = getIssuerTag(issuerId, issuerType);

  const {
    data: company,
    isLoading: isLoadingCompany,
    isError: isErrorCompany,
    refetch: refetchCompany,
    error,
  } = useFetchIssuer(issuerTag);

  const {
    data: pageComponentsMap,
    isLoading: isLoadingComponents,
    isError: isErrorComponents,
    refetch: refetchComponents,
  } = useFetchPageComponents(issuerTag, {
    enabled: !!(company?.canAccess && company?.canAccessCompanyPage && company?.hasCompanyPage),
  });
  const { isLoading: isLoadingTagFilters } = useFetchFilters([]);
  const { isLoading: isLoadingAnalysts } = useFetchAnalysts({
    analystIds: company?.analysts || [],
  });
  const { data: userPermissions } = useFetchUserPermissions();
  const { data: userProfile } = useFetchUserProfile();
  const {
    isError: isErrorDeals,
    data: deals,
    isLoading: isLoadingDeals,
  } = useFetchCompanyDeals(issuerId, {
    take: 1,
    enabled: company?.canAccess,
  });
  const {
    isError: isDocumentsError,
    bondsTotalResults,
    loansTotalResults,
    filingsTotalResults,
    isLoading: isDocumentsLoading,
  } = useFetchCompanyDocuments(issuerId);

  const tabConfig = getDealsTabs(userPermissions!);
  const marketStatuses = [DealMarketStatus.InMarket];
  if (tabConfig.includes(DealMarketStatus.Expected)) {
    marketStatuses.push(DealMarketStatus.Expected);
  }
  const {
    isLoading: isLoadingWidgetDeals,
    isError: isErrorWidgetDeals,
    refetch: refetchWidgetDeals,
    data: widgetDeals,
  } = useFetchCompanyDeals(issuerId, {
    marketStatuses,
    take: 1,
    enabled: company?.canAccess,
  });
  const {
    data: docketCases,
    isLoading: isLoadingDocketCases,
    isError: isErrorDocketCases,
  } = useFetchCases(issuerId, hasPermissionToDockets(userPermissions));

  const isAskAnAnalystButtonVisible = isAAAButtonVisible(company, userPermissions);
  const hasAccessToFinancials = hasPermissionToFinancials(userPermissions);
  const hasCoreScoresPermission = hasPermissionToCoreScores(userPermissions);
  const { data: riskMetrics } = useFetchRiskProducts(issuerId, { enabled: true }, undefined, true);
  const { data: financialsDto } = useFetchFinancials(issuerId, !hasAccessToFinancials);
  const segmentGroups = useFetchFinancialsSegments(financialsDto);

  const showRiskProductsWidget = !!riskMetrics && hasCompanyRiskMetrics(riskMetrics.cqsMetrics, riskMetrics.creMetrics);

  const scoreData = setScoreDataFormat(company?.coreScores);
  const showScoresWidget = scoreData.isScore && hasCoreScoresPermission;

  const { userFullName, disclaimer, redistributionAllowed, customLogoUrl, customSupportText } = useFetchForPrint();
  const {
    data: companySnapshotData,
    refetch: downloadCompanySnapshot,
    isFetching: downloadCompanySnapshotLoading,
    remove: removeCompanySnapshot,
  } = useFetchCompanySnapshot(
    issuerId,
    userProfile?.staff ? '' : userFullName,
    getCustomDealPermissions(userPermissions)
  );

  const isLoading =
    isLoadingCompany ||
    isLoadingComponents ||
    isLoadingTagFilters ||
    isLoadingAnalysts ||
    isLoadingDeals ||
    isLoadingWidgetDeals ||
    isDocumentsLoading;

  const isError = isErrorCompany || isErrorComponents || isErrorWidgetDeals;

  const refetch = async () => {
    if (isErrorCompany) {
      await refetchCompany();
    }
    if (isErrorComponents) {
      await refetchComponents();
    }
    if (isErrorWidgetDeals) {
      await refetchWidgetDeals();
    }
  };

  const { pathname } = useLocation();
  const companyCovered = isCompanyCovered(company);
  const hasDeals = isErrorDeals || !isEmpty(deals);
  const hasWidgetDeals = !isEmpty(widgetDeals);
  const hasFinancialsCoverage = isFinancialsCovered(company);
  const hasFinancials = hasFinancialsCoverage && !isEmpty(segmentGroups);

  const isDocumentsTabVisible = isDocumentsError || !!(bondsTotalResults + loansTotalResults + filingsTotalResults);
  const isDocketsTabVisible =
    hasPermissionToDockets(userPermissions) && (!isEmpty(docketCases) || isErrorDocketCases || isLoadingDocketCases);
  const isRiskTabVisible = isRpCovered(company);
  const isCoveredRPOnly = isCompanyCoveredRPOnly(company);
  const canAddToWatchlist = userPermissions?.hasAccessToMyContentWatchlists();
  const isDownloadSnapshotVisible =
    userPermissions?.permissions.includes('ui:tearsheets:research') && company?.hasSnapshot;
  const isFinancialsTabVisible = !(company?.hideKeyMetrics && company?.hideAsReported);
  useEffect(() => {
    if (isLoading) {
      return;
    }
    if (isNotFound(error)) {
      navigate(ROUTES.notFound, { replace: true });
      return;
    }
    if (isCompanyRoute(issuerId, pathname)) {
      const routes = getIssuerRoutes(
        issuerType,
        issuerId,
        companyCovered,
        hasFinancialsCoverage,
        isRiskTabVisible,
        hasDeals,
        isDocumentsTabVisible,
        isDocketsTabVisible,
        isCoveredRPOnly,
        userPermissions,
        isFinancialsTabVisible
      );
      if (routes[0] && routes[0].to !== pathname) {
        navigate(routes[0].to, { replace: true });
        return;
      }
    }
    if (
      (isDealsRoute(issuerId, pathname) && !hasDeals) ||
      (isDocketsRoute(issuerId, pathname) && !isDocketsTabVisible)
    ) {
      navigate(ROUTES.notFound, { replace: true });
    }
  }, [company, isLoading, error, pathname, isDocketsTabVisible, isCoveredRPOnly]);

  const tabClickHandler = (event) => {
    trackCompanyTabView(event?.target.innerText, issuerId);
  };

  useEffect(() => {
    if (companySnapshotData) {
      const { data, headers } = companySnapshotData;
      const filenameMatch = headers['content-disposition'].match(/filename="([^"]+)"/);
      const [, filename] = filenameMatch;

      const downloadLink = document.createElement('a');
      downloadLink.href = window.URL.createObjectURL(data);
      downloadLink.download = filename;
      downloadLink.click();
      downloadLink.remove();
    }

    return () => {
      removeCompanySnapshot();
    };
  }, [companySnapshotData]);

  const renderCompany = () => {
    if (!company) {
      return null;
    }
    const decodedPathName = safeDecode(pathname);
    const isOverviewActiveTab: boolean =
      (decodedPathName === ROUTES.company(issuerId) || decodedPathName === ROUTES.company(`${issuerId}/`)) &&
      companyCovered;
    return (
      <>
        <div className={cx('header-print')}>
          <CompanyHeader name={company.displayName} ticker={company.displayTicker} />
          <AnalystsWidget analysts={company.analysts} print />
          <Divider />
        </div>
        <div className={cx('header')}>
          <CompanyHeader className="no-print" name={company.displayName} ticker={company.displayTicker} />
          <div className={cx('action-buttons-wrapper')}>
            {companyCovered && (
              <>
                <CoverageIndicator className="no-print" {...company} riskProductsCoverage={isRpCovered(company)} />
                <Divider vertical className="no-print" />
              </>
            )}
            <AnalystsWidget
              analysts={company.analysts}
              minimized
              setIsAnalystsWidgetRendered={setIsAnalystsWidgetRendered}
              hideDeactivatedAnalyst
              askAnalystModalSubject={{
                companyId: issuerTag,
                subjectName: company.displayName,
              }}
              showAskAnAnalyst={isAskAnAnalystButtonVisible}
            />
            {isAnalystsWidgetRendered && <Divider vertical className="no-print" />}
            {isDownloadSnapshotVisible && (
              <CompanyDownloadSnapshotAction
                onClick={() => downloadCompanySnapshot()}
                loading={downloadCompanySnapshotLoading}
              />
            )}
            {isOverviewActiveTab && (
              <CompanyPrintAction
                companyPrintViewRef={companyPrintViewRef}
                pageComponentsMap={pageComponentsMap}
                company={company}
                hiddenSections={{
                  [STATIC_PRINTABLE_SECTIONS.DEALS.id]: !hasWidgetDeals,
                  [STATIC_PRINTABLE_SECTIONS.RISK_PRODUCTS.id]: !showRiskProductsWidget,
                  [STATIC_PRINTABLE_SECTIONS.CS_VIEW.id]: hideCSView(company),
                  [STATIC_PRINTABLE_SECTIONS.CORE_SCORE.id]: !showScoresWidget,
                }}
                selectedSectionsForPrint={selectedSectionsForPrint}
                setSelectedSectionsForPrint={setSelectedSectionsForPrint}
              />
            )}
            {canAddToWatchlist && <CompanyAddToWatchlistAction issuerTag={issuerTag} />}
          </div>
          <div className={cx('tabs-wrapper')} data-testid="company-tabs-wrapper">
            <LinkGroup
              routes={getIssuerRoutes(
                issuerType,
                issuerId,
                companyCovered,
                isFinancialsCovered(company),
                isRiskTabVisible,
                hasDeals,
                isDocumentsTabVisible,
                isDocketsTabVisible,
                isCoveredRPOnly,
                userPermissions,
                isFinancialsTabVisible
              )}
              onRouteClick={(event) => tabClickHandler(event)}
            />
          </div>
        </div>
        <Outlet context={{ issuerTag, company }} />
        {isOverviewActiveTab && (
          <CompanyOverviewTab
            company={company}
            showScoresWidget={showScoresWidget}
            pageComponentsMap={pageComponentsMap}
            hasDeals={hasWidgetDeals}
            hasFinancials={hasFinancials}
            hasRiskProducts={showRiskProductsWidget && isRiskTabVisible}
            selectedSectionsForPrint={selectedSectionsForPrint}
          />
        )}
      </>
    );
  };

  // Redirect from companies/talen_energy => companies/101541
  // Otherwise some widgets/tabs not render properly as they cannot fetch data based on "non-cscompany" tagId
  // The issue should be probably addressed elsewhere but due to urgency it been fixed here..
  if (!!company && company?.tagId !== issuerTag) {
    return <Navigate to={getIssuerRoute(company.tagId)} />;
  }

  if (isLoading) {
    return (
      <div className={cx('body')}>
        <div className={cx('loader-container')}>
          <Loader position="center" />
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={cx('body')}>
        <DataLoadError onRetryClick={refetch} />
      </div>
    );
  }

  return (
    <RecentlyPublishedContextProvider>
      <CompanyDealsContextProvider>
        <CompanyRiskProductsContextProvider>
          <div className={cx('main-body')}>
            <PrintView
              ref={companyPrintViewRef}
              isHeaderFooterVisible
              userFullName={userFullName}
              disclaimer={disclaimer}
              redistributionAllowed={redistributionAllowed}
              customLogoUrl={customLogoUrl}
              customSupportText={customSupportText}
            >
              <div className={cx('body')}>{renderCompany()}</div>
            </PrintView>
          </div>
        </CompanyRiskProductsContextProvider>
      </CompanyDealsContextProvider>
    </RecentlyPublishedContextProvider>
  );
};

export default Company;
